import React, { useCallback, useEffect, useState } from 'react';
import FaIcon from '../../../components/FaIcon';
import { Button, Modal, ModalBody, ModalHeader, Spinner } from 'reactstrap';
import { UPLOAD_LIMIT_WORDS } from './media_refusals';
import {
    HELD_WORDS as W,
    chip_media_ids,
    drop_held,
    error_tag,
    fetch_held,
    held_added_line,
    held_at_limit,
    held_clears_line,
    held_count_line,
    held_drop_refusal,
    held_log,
    held_size,
    held_type_label,
    held_without,
    is_no_held_route,
    parse_held_list,
    subscribe_chips,
} from './held_files';

/**
 * "Files waiting to be sent" (CT-15747): the upload limit's line under a composer with "See files", and the list it
 * opens — the files this representative holds for messages not sent yet, the count against the limit, Remove on each.
 * Every rule and word is held_files.js's; this file draws them in the CSP's style (reactstrap, inline styles).
 */

/** The upload limit's line (today's words), with "See files" when the api lists the files. */
export function UploadLimitLine({ see_files, on_see_files, on_dismiss }) {
    return (
        <div data-testid='held-limit-line' role='status'
            style={{ marginTop: 8, fontSize: 11.5, color: '#a94442', background: '#fdf1f0', border: '1px solid #f3d3cd', borderRadius: 8, padding: '5px 10px', display: 'flex', gap: 8, alignItems: 'flex-start' }}>
            <span style={{ flex: 1 }}>
                {UPLOAD_LIMIT_WORDS}
                {see_files ? (
                    <>
                        {' '}
                        <button type='button' data-testid='held-see-files' title={W.see_files_hint} onClick={on_see_files}
                            style={{ border: 0, background: 'none', padding: 0, color: '#2f6fed', fontWeight: 600, fontSize: 'inherit', cursor: 'pointer', textDecoration: 'underline' }}>
                            {W.see_files}
                        </button>
                    </>
                ) : null}
            </span>
            <button type='button' aria-label={W.dismiss} title={W.dismiss} data-testid='held-limit-dismiss' onClick={on_dismiss}
                style={{ border: 0, background: 'none', padding: 0, color: '#e74c3c', cursor: 'pointer', fontSize: 'inherit' }}><FaIcon name='times' /></button>
        </div>
    );
}

/** The composers' chips, live (rule 3). */
function use_chips() {
    const [ids, set_ids] = useState(() => chip_media_ids());
    useEffect(() => subscribe_chips(() => set_ids(chip_media_ids())), []);
    return ids;
}

/**
 * The list itself: read from the api when it mounts (and on Try again); the server's answer is drawn. `on_no_route`
 * runs when the api answers as one from before the routes (the line then drops "See files").
 */
export function HeldFilesList({ on_no_route }) {
    const [load, set_load] = useState({ status: 'loading' });
    const [marks, set_marks] = useState({}); // mediaId → 'removing' | 'failed'
    const [quiet, set_quiet] = useState(null);
    const [tries, set_tries] = useState(0);
    const in_message = use_chips();

    useEffect(() => {
        let live = true;
        set_load({ status: 'loading' });
        fetch_held()
            .then((raw) => {
                const list = parse_held_list(raw);
                if (!list) held_log('the files waiting came back in a shape the CSP does not read');
                if (live) set_load(list ? { status: 'done', list } : { status: 'failed', words: W.load_failed });
            })
            .catch((e) => {
                held_log(`the files waiting did not load (${error_tag(e)})`);
                if (!live) return;
                if (is_no_held_route(e)) {
                    if (on_no_route) on_no_route();
                    set_load({ status: 'failed', words: W.not_on_server });
                } else set_load({ status: 'failed', words: W.load_failed });
            });
        return () => { live = false; };
        // on_no_route: read when the answer lands; a new function each render must not read the list again.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tries]);

    const mark = useCallback((id, m) => set_marks((prev) => {
        const next = { ...prev };
        if (m) next[id] = m; else delete next[id];
        return next;
    }), []);

    const remove = async (id, name) => {
        // Rule 3, read again at the press: a file a composer holds is never dropped from here.
        if (chip_media_ids().has(id)) return;
        mark(id, 'removing');
        set_quiet(null);
        try {
            const done = await drop_held(id);
            set_load((l) => (l.status === 'done'
                ? { status: 'done', list: { limit: done.limit, count: done.count, files: l.list.files.filter((f) => f.mediaId !== id) } }
                : l));
            mark(id, null);
        } catch (e) {
            held_log(`a file waiting was not removed (${error_tag(e)})`);
            const refusal = held_drop_refusal(e);
            if (refusal === 'failed') { mark(id, 'failed'); return; }
            set_load((l) => (l.status === 'done' ? { status: 'done', list: held_without(l.list, id) } : l));
            mark(id, null);
            if (refusal === 'already-sent') set_quiet(`${W.already_sent}: ${name}`);
        }
    };

    if (load.status === 'loading') {
        return <div data-testid='held-loading' style={{ padding: 16, color: '#7f8c8d', fontSize: 13 }}><Spinner size='sm' style={{ width: 12, height: 12 }} /> {W.loading}</div>;
    }
    if (load.status === 'failed') {
        return (
            <div data-testid='held-failed' style={{ padding: 16, color: '#7f8c8d', fontSize: 13 }}>
                <div>{load.words}</div>
                {load.words === W.load_failed ? (
                    <Button size='sm' color='light' style={{ marginTop: 8 }} data-testid='held-retry' onClick={() => set_tries((t) => t + 1)}>{W.try_again}</Button>
                ) : null}
            </div>
        );
    }
    const { list } = load;
    const now = Date.now();
    const full = held_at_limit(list.count, list.limit);
    return (
        <div data-testid='held-list'>
            <div style={{ padding: '4px 0 10px', borderBottom: '1px solid #ecf0f1' }}>
                <div data-testid='held-count' aria-label={`${held_count_line(list.count, list.limit)} ${W.count_of}`}
                    style={{ fontSize: 20, fontWeight: 700, color: full ? '#d35400' : '#2c3e50' }} data-full={full ? 'yes' : 'no'}>
                    {held_count_line(list.count, list.limit)}
                </div>
                <div style={{ fontSize: 12, color: '#7f8c8d' }}>{W.about(list.limit)}</div>
            </div>
            {quiet ? <div data-testid='held-quiet' role='status' style={{ fontSize: 12, color: '#7f8c8d', background: '#f6f8f9', padding: '6px 10px', marginTop: 8, borderRadius: 6 }}>{quiet}</div> : null}
            {list.files.length === 0 ? (
                <div data-testid='held-empty' style={{ padding: '24px 0', textAlign: 'center', color: '#7f8c8d', fontSize: 13 }}>{W.empty}</div>
            ) : (
                <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                    {list.files.map((f) => {
                        const name = f.filename.trim() === '' ? W.no_name : f.filename;
                        const meta = [held_type_label(f.mimetype, f.filename), held_size(f.size), held_added_line(f.createdAt, now)].filter((s) => s !== '').join(' · ');
                        const clears = held_clears_line(f.expiresAt, now);
                        const held = in_message.has(f.mediaId);
                        const m = marks[f.mediaId];
                        return (
                            <li key={f.mediaId} data-testid='held-row' data-media-id={f.mediaId}
                                style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '10px 0', borderBottom: '1px solid #ecf0f1' }}>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ fontWeight: 600, fontSize: 13, overflowWrap: 'anywhere' }}><FaIcon name='paperclip' label='File' /> {name}</div>
                                    <div style={{ fontSize: 12, color: '#7f8c8d' }}>{meta}</div>
                                    {clears ? <div style={{ fontSize: 12, color: '#7f8c8d' }}>{clears}</div> : null}
                                    {held ? (
                                        <div data-testid='held-in-message' style={{ marginTop: 4, fontSize: 12 }}>
                                            <span style={{ display: 'inline-block', fontWeight: 600, fontSize: 11, color: '#2f6fed', background: '#eef4ff', borderRadius: 999, padding: '1px 8px' }}>{W.in_message}</span>
                                            {' '}<span style={{ color: '#7f8c8d' }}>{W.in_message_hint}</span>
                                        </div>
                                    ) : null}
                                    {m === 'failed' ? <div data-testid='held-remove-failed' role='status' style={{ marginTop: 4, fontSize: 12, color: '#c0392b' }}>{W.remove_failed}</div> : null}
                                </div>
                                {held ? null : (
                                    <Button size='sm' color='light' data-testid='held-remove' aria-label={W.remove_label(name)} disabled={m === 'removing'}
                                        onClick={() => remove(f.mediaId, name)}>
                                        {m === 'removing' ? W.removing : W.remove}
                                    </Button>
                                )}
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
}

/** The list in a modal, for a composer that is not itself in one (the Thread). */
export function HeldFilesModal({ open, on_close, on_no_route }) {
    return (
        <Modal isOpen={open} toggle={on_close}>
            <ModalHeader toggle={on_close} style={{ fontSize: 15 }}>{W.title}</ModalHeader>
            <ModalBody style={{ paddingTop: 8, maxHeight: '70vh', overflowY: 'auto' }}>
                {open ? <HeldFilesList on_no_route={on_no_route} /> : null}
            </ModalBody>
        </Modal>
    );
}
